import React, { useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

interface SpaceCursorProps {
  isDark: boolean;
}

const glow = keyframes`
  0% {
    filter: drop-shadow(0 0 2px #00ffff) drop-shadow(0 0 4px #00ffff);
  }
  50% {
    filter: drop-shadow(0 0 4px #00ffff) drop-shadow(0 0 8px #00ffff);
  }
  100% {
    filter: drop-shadow(0 0 2px #00ffff) drop-shadow(0 0 4px #00ffff);
  }
`;

const float = keyframes`
  0% {
    transform: translate(-50%, -50%) translateY(0);
  }
  50% {
    transform: translate(-50%, -50%) translateY(-2px);
  }
  100% {
    transform: translate(-50%, -50%) translateY(0);
  }
`;

const CursorContainer = styled.div`
  position: fixed;
  width: 32px;
  height: 32px;
  pointer-events: none;
  z-index: 9999;
  mix-blend-mode: difference;
  will-change: transform;
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;
  perspective: 1000px;
`;

const Arrow = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 12px solid #00ffff;
  transform: translate(-50%, -50%);
  animation: ${float} 2s ease-in-out infinite;
  will-change: transform;
`;

const GlowEffect = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 24px;
  height: 24px;
  background: radial-gradient(circle, rgba(0,255,255,0.2) 0%, rgba(0,255,255,0) 70%);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  animation: ${glow} 2s ease-in-out infinite;
  will-change: transform;
`;

const Particle = styled.div<{ delay: number; size: number }>`
  position: absolute;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  background: #00ffff;
  border-radius: 50%;
  opacity: 0.6;
  animation: ${float} 2s ease-in-out infinite;
  animation-delay: ${props => props.delay}s;
  will-change: transform;
`;

const SpaceCursor: React.FC<SpaceCursorProps> = ({ isDark }) => {
  const cursorRef = useRef<HTMLDivElement>(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const cursorPosRef = useRef({ x: 0, y: 0 });
  const animationFrameRef = useRef<number>();

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };

    const animate = () => {
      // Smooth cursor movement
      cursorPosRef.current.x += (mouseRef.current.x - cursorPosRef.current.x) * 0.2;
      cursorPosRef.current.y += (mouseRef.current.y - cursorPosRef.current.y) * 0.2;

      if (cursorRef.current) {
        cursorRef.current.style.transform = `translate3d(${cursorPosRef.current.x}px, ${cursorPosRef.current.y}px, 0)`;
      }

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    window.addEventListener('mousemove', handleMouseMove);
    animate();

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return (
    <CursorContainer ref={cursorRef}>
      <GlowEffect />
      <Arrow />
      <Particle delay={0} size={2} style={{ top: '20%', left: '30%' }} />
      <Particle delay={0.2} size={1.5} style={{ top: '40%', left: '60%' }} />
      <Particle delay={0.4} size={1} style={{ top: '60%', left: '20%' }} />
    </CursorContainer>
  );
};

export default SpaceCursor; 